import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase/client';
import { useAuthSession } from '../../hooks/useAuthSession';

interface ProfileCompletionGuardProps {
  children: React.ReactNode;
}

const ProfileCompletionGuard: React.FC<ProfileCompletionGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, userRole: contextUserRole, isLoading: sessionLoading } = useAuthSession(); // Get from context with loading state!
  const [loading, setLoading] = useState(true);
  const checkInProgress = useRef(false);

  const checkProfileCompletion = useCallback(async () => {
    // Prevent multiple simultaneous checks
    if (checkInProgress.current) {
      return;
    }
    
    checkInProgress.current = true;
    setLoading(true); // Set loading while checking
    
    try {
      // Use session from context instead of fetching it again!
      const user = session?.user || null;
      
      if (!user) {
        navigate('/login');
        checkInProgress.current = false;
        return;
      }
      
      // Use role from context (already computed by AuthSessionProvider)
      const role = contextUserRole || user.user_metadata?.role || null;
      
      // Special handling for CSR and admin users - they don't need profile checks
      if (role === 'csr' || role === 'admin') {
        setLoading(false);
        checkInProgress.current = false;
        return;
      }

      // Check if profile is completed (only for regular users)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('profile_completed, full_name, user_display_id, company_id')
        .eq('id', user.id)
        .single();


      // If no user record exists (PGRST116 = not found), try auto-completing the profile
      // IMPORTANT: Only attempt auto-complete for truly missing records, not other errors
      if (userError && userError.code === 'PGRST116') {
        console.log('ProfileCompletionGuard: No user record found (PGRST116), attempting auto-complete...');
        
        try {
          const { data, error } = await supabase.functions.invoke('autoCompleteProfile');
          
          if (!error && data?.success) {
            console.log('ProfileCompletionGuard: Profile auto-completed successfully');
            // Re-check the profile after auto-completion
            const { data: newUserData } = await supabase
              .from('users')
              .select('profile_completed, full_name, user_display_id, company_id')
              .eq('id', user.id)
              .single();
              
            if (newUserData) {
              // Update userData for the completion check below
              Object.assign(userData || {}, newUserData);
            }
          } else {
            console.error('ProfileCompletionGuard: Auto-complete failed:', error);
            // Only redirect to manual completion for new users
            if (error?.message?.includes('Profile already complete')) {
              // User exists but might have incomplete profile
              console.log('ProfileCompletionGuard: User exists, checking profile completion...');
            } else {
              // Fall back to manual profile completion for truly new users
              navigate('/complete-profile');
              return;
            }
          }
        } catch (autoCompleteError) {
          console.error('ProfileCompletionGuard: Error calling autoCompleteProfile:', autoCompleteError);
          // Don't automatically redirect, let the completion check below handle it
        }
      } else if (userError) {
        // For other errors, log but don't attempt auto-complete
        console.error('ProfileCompletionGuard: Error fetching user profile:', userError);
        // For CSR users or admin, allow access even with profile errors
        // CSRs may not have complete profiles as they're special internal users
        if (role === 'csr' || role === 'admin') {
          console.log('ProfileCompletionGuard: CSR/Admin user with profile query error, allowing access anyway');
          setLoading(false);
          return;
        }
        // For regular users with errors, also stop loading to prevent infinite spinner
        console.log('ProfileCompletionGuard: Profile query error for regular user, stopping load');
        setLoading(false);
        return;
      }

      const isCompleted = userData?.profile_completed && 
                        userData?.full_name && 
                        userData?.user_display_id && 
                        userData?.company_id;

      console.log('ProfileCompletionGuard: Profile completion status:', {
        profile_completed: userData?.profile_completed,
        full_name: !!userData?.full_name,
        user_display_id: !!userData?.user_display_id,
        company_id: !!userData?.company_id,
        isCompleted
      });

      // Redirect to profile completion if needed (except for logout and profile routes)
      if (!isCompleted && 
          !location.pathname.includes('/complete-profile') && 
          !location.pathname.includes('/logout')) {
        console.log('ProfileCompletionGuard: Profile incomplete, redirecting to complete-profile');
        navigate('/complete-profile');
        return;
      }

      setLoading(false);
      checkInProgress.current = false;
    } catch (error) {
      console.error('ProfileCompletionGuard: Error:', error);
      setLoading(false);
      checkInProgress.current = false;
    } finally {
      // Ensure we always reset the in-progress flag
      if (checkInProgress.current) {
        checkInProgress.current = false;
      }
    }
  }, [navigate, session, contextUserRole, location.pathname]); // Dependencies: navigate for redirects, session and role from context, location for path checks

  useEffect(() => {
    // Wait for session to finish loading from AuthSessionProvider
    if (!sessionLoading && !checkInProgress.current) {
      checkProfileCompletion();
    }
  }, [sessionLoading, checkProfileCompletion]);

  // Show loading if either session is still loading from context OR profile check is in progress
  if (sessionLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProfileCompletionGuard;